import { Modal } from "react-bootstrap";
import { Formik, Field, Form, ErrorMessage } from "formik";
import Select from "react-select";
import * as Yup from "yup";
import { bouncy } from "ldrs";
import { useGrievanceCategoryStore } from "./GrievanceCategoryStore/useGrievanceCategoryStore";

bouncy.register();

const CATEGORY_FORBIDDEN_CHARS_PATTERN = /[@#$%^&*<>]/;
const CATEGORY_ONLY_NUMBERS_PATTERN = /^[0-9\s]+$/;
const CATEGORY_NAME_PATTERN = /^[a-zA-Z0-9\s\-()]+$/;

const cancelButtonStyle = {
  padding: "8px 20px",
  borderRadius: "4px",
  border: "1px solid #1e40af",
  background: "#ffffff",
  color: "#1e40af",
  fontSize: 14,
  fontWeight: 500,
  cursor: "pointer",
};

const saveButtonStyle = {
  padding: "8px 20px",
  borderRadius: "4px",
  border: "1px solid #0f172a",
  background: "#0f172a",
  color: "#ffffff",
  fontSize: 14,
  fontWeight: 500,
  cursor: "pointer",
  minWidth: 72,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};

const AddEditGrievanceForm = ({
  showModal,
  setShowModal,
  editRow,
  isEditMode,
}) => {
  const {
    grievanceCategories,
    slaCategoryTypeOptions,
    isSaving,
    saveGrievanceCategory,
  } = useGrievanceCategoryStore();

  const handleClose = () => {
    setShowModal(false);
  };

  const initialValues = {
    categoryName: isEditMode ? editRow?.categoryName || "" : "",
    slacategoryId: isEditMode ? editRow?.slacategoryId?.toString() || "" : "",
    isActive: isEditMode ? editRow?.isActive?.toString() || "true" : "true",
  };

  const validationSchema = Yup.object().shape({
    categoryName: Yup.string()
      .trim()
      .required("Category Name is required")
      .min(3, "Minimum 3 characters required.")
      .max(100, "Maximum 100 characters allowed.")
      .test(
        "not-only-numbers",
        "Category Name cannot contain only numbers.",
        (value) => !value || !CATEGORY_ONLY_NUMBERS_PATTERN.test(value)
      )
      .test(
        "no-special-chars",
        "Special characters such as @, #, $, %, ^, &, *, <, > shall not be allowed.",
        (value) =>
          !value ||
          (!CATEGORY_FORBIDDEN_CHARS_PATTERN.test(value) &&
            CATEGORY_NAME_PATTERN.test(value))
      )
      // .test(
      //   "unique-category",
      //   "Category name already exists",
      //   function (value) {
      //     if (!value) return true;
      //     const trimmedName = value.trim();
      //     return !grievanceCategories.some(
      //       (item) =>
      //         item.categoryName?.toLowerCase() === trimmedName.toLowerCase() &&
      //         item.grievanceCategoryId !== editRow?.grievanceCategoryId
      //     );
      //   }
      // )
      ,
    slacategoryId: Yup.string().required("SLA Category Type is required"),
    isActive: Yup.string().required("Status is required"),
  });

  const handleSubmit = async (values) => {
    const payload = {
      categoryName: values.categoryName.trim(),
      slacategoryId: Number(values.slacategoryId),
      isActive: values.isActive === "true",
    };

    if (isEditMode) {
      payload.grievanceCategoryId = editRow.grievanceCategoryId;
    }

    const result = await saveGrievanceCategory(payload, isEditMode);

    if (result?.success) {
      handleClose();
    }
  };

  return (
    <Modal
      size="md"
      show={showModal}
      onHide={handleClose}
      aria-labelledby="grievance-category-modal-title"
      backdrop="static"
      centered
    >
      <Modal.Header>
        <Modal.Title id="grievance-category-modal-title">
          {isEditMode ? "Edit Grievance Category" : "Add Grievance Category"}
        </Modal.Title>
        <i
          className="fa-solid fa-xmark close-container"
          onClick={handleClose}
          role="button"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              handleClose();
            }
          }}
          aria-label="Close modal"
        ></i>
      </Modal.Header>
      <hr className="mt-4" />
      <Modal.Body>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, setFieldValue }) => (
            <Form>
              <div className="mb-3">
                <label htmlFor="categoryName" className="form-label">
                  Category Name
                  <span style={{ color: "#EC1117" }}> *</span>
                </label>
                <Field
                  type="text"
                  name="categoryName"
                  id="categoryName"
                  placeholder="Enter Category Name"
                  className="form-control"
                  maxLength={100}
                  onChange={(event) => {
                    const formattedValue = event.target.value
                      .slice(0, 100)
                      .replace(/^\s+/, "")
                      .replace(/\s{2,}/g, " ")
                      .replace(CATEGORY_FORBIDDEN_CHARS_PATTERN, "")
                      .replace(/[^a-zA-Z0-9\s\-()]/g, "");
                    setFieldValue("categoryName", formattedValue);
                  }}
                />
                <ErrorMessage
                  className="required-message"
                  name="categoryName"
                  component="div"
                />
              </div>

              <div className="mb-3">
                <label htmlFor="slacategoryId" className="form-label">
                  SLA Category Type
                  <span style={{ color: "#EC1117" }}> *</span>
                </label>
                <Select
                  inputId="slacategoryId"
                  name="slacategoryId"
                  classNamePrefix="react-select"
                  options={slaCategoryTypeOptions.map((option) => ({
                    value: String(option.id),
                    label: option.label,
                  }))}
                  value={
                    slaCategoryTypeOptions
                      .map((option) => ({
                        value: String(option.id),
                        label: option.label,
                      }))
                      .find(
                        (option) => option.value === String(values.slacategoryId)
                      ) || null
                  }
                  onChange={(selected) =>
                    setFieldValue("slacategoryId", selected ? selected.value : "")
                  }
                  placeholder="Select SLA Category Type"
                  isClearable={false}
                  menuPlacement="auto"
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                />
                <ErrorMessage
                  className="required-message"
                  name="slacategoryId"
                  component="div"
                />
              </div>

              <div className="mb-3">
                <label className="form-label">
                  Status
                  <span style={{ color: "#EC1117" }}> *</span>
                </label>
                <div className="form-group d-flex">
                  <div className="form-check">
                    <Field
                      type="radio"
                      className="form-check-input"
                      name="isActive"
                      id="grievanceActiveRadio"
                      value="true"
                    />
                    <label
                      className="form-check-label"
                      htmlFor="grievanceActiveRadio"
                    >
                      Active
                    </label>
                  </div>
                  <div className="form-check ms-4">
                    <Field
                      type="radio"
                      className="form-check-input"
                      name="isActive"
                      id="grievanceInactiveRadio"
                      value="false"
                    />
                    <label
                      className="form-check-label"
                      htmlFor="grievanceInactiveRadio"
                    >
                      Inactive
                    </label>
                  </div>
                </div>
                <ErrorMessage
                  className="required-message"
                  name="isActive"
                  component="div"
                />
              </div>

              <hr />
              <div className="d-flex justify-content-end gap-2">
                <button
                  type="button"
                  style={cancelButtonStyle}
                  onClick={handleClose}
                >
                  Cancel
                </button>
                <button type="submit" style={saveButtonStyle} disabled={isSaving}>
                  {isSaving ? (
                    <l-bouncy size="22" speed="1.75" color="white"></l-bouncy>
                  ) : isEditMode ? (
                    "Update"
                  ) : (
                    "Save"
                  )}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </Modal.Body>
    </Modal>
  );
};

export default AddEditGrievanceForm;
